import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { PhoneCall, Clock, Info, Save } from "lucide-react";
import { toast } from "sonner";

/** Dialer configuration form for the active tenant.
 *  Persists to /api/lead-dialer-config (one row per tenant). Genesys IDs are
 *  placeholders until Phase 7 lands. */
export default function DialerConfigForm() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/lead-dialer-config");
      setForm(data);
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load config");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
    setDirty(true);
  };

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      const payload = {
        max_attempts: Number(form.max_attempts),
        ring_duration_seconds: Number(form.ring_duration_seconds),
        gap_between_attempts_hours: Number(form.gap_between_attempts_hours),
        daily_call_window_start: form.daily_call_window_start,
        daily_call_window_end: form.daily_call_window_end,
        call_on_weekends: !!form.call_on_weekends,
        missed_callback_grace_minutes: Number(form.missed_callback_grace_minutes),
        auto_close_disposition: form.auto_close_disposition || "NR",
        caller_id: form.caller_id || null,
        genesys_campaign_id: form.genesys_campaign_id || null,
      };
      await api.put("/lead-dialer-config", payload);
      toast.success("Dialer config saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="text-slate-400 p-4 text-sm">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="dialer-config">
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <PhoneCall className="w-4 h-4 text-indigo-600" />
              <h3 className="font-heading text-sm font-semibold">Dialer Configuration</h3>
            </div>
            <Button
              onClick={save}
              disabled={!dirty || saving}
              size="sm"
              className="h-8 bg-indigo-600 hover:bg-indigo-700"
              data-testid="dialer-save-btn"
            >
              <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save"}
            </Button>
          </div>
          <p className="text-xs text-slate-500 -mt-2">
            Controls outbound calling cadence, ring timeouts, and auto-close behaviour.
            Genesys fields are placeholders until the integration is live (Phase 7).
          </p>

          {/* Attempts + gap */}
          <div className="grid grid-cols-2 gap-3">
            <NumField
              label="Max attempts" min={1} max={20}
              value={form.max_attempts} onChange={(v) => update({ max_attempts: v })}
              testid="dialer-max-attempts"
              hint="Auto-closes as configured disposition after N failed dials"
            />
            <NumField
              label="Gap between attempts (hours)" min={0} max={168}
              value={form.gap_between_attempts_hours} onChange={(v) => update({ gap_between_attempts_hours: v })}
              testid="dialer-gap-hours"
              hint="Minimum wait before the same lead is dialed again"
            />
            <NumField
              label="Ring duration (seconds)" min={5} max={180}
              value={form.ring_duration_seconds} onChange={(v) => update({ ring_duration_seconds: v })}
              testid="dialer-ring-seconds"
            />
            <NumField
              label="Missed callback grace (mins)" min={0} max={1440}
              value={form.missed_callback_grace_minutes} onChange={(v) => update({ missed_callback_grace_minutes: v })}
              testid="dialer-grace-mins"
              hint="Grace period after a scheduled callback slips before flagging"
            />
          </div>

          {/* Time window */}
          <div className="grid grid-cols-3 gap-3 pt-2 border-t">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">Window start (Asia/Qatar)</Label>
              <Input
                type="time"
                value={form.daily_call_window_start}
                onChange={(e) => update({ daily_call_window_start: e.target.value })}
                className="h-8 text-xs mt-1"
                data-testid="dialer-window-start"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">Window end (Asia/Qatar)</Label>
              <Input
                type="time"
                value={form.daily_call_window_end}
                onChange={(e) => update({ daily_call_window_end: e.target.value })}
                className="h-8 text-xs mt-1"
                data-testid="dialer-window-end"
              />
            </div>
            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 text-xs">
                <Switch
                  checked={!!form.call_on_weekends}
                  onCheckedChange={(v) => update({ call_on_weekends: v })}
                  data-testid="dialer-weekends"
                />
                <span>Call on weekends (Fri/Sat)</span>
              </label>
            </div>
          </div>

          {/* Auto-close + Caller ID */}
          <div className="grid grid-cols-2 gap-3 pt-2 border-t">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">Auto-close disposition</Label>
              <Select
                value={form.auto_close_disposition || "NR"}
                onValueChange={(v) => update({ auto_close_disposition: v })}
              >
                <SelectTrigger className="h-8 text-xs mt-1" data-testid="dialer-autoclose">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NR">NR · Not Reachable</SelectItem>
                  <SelectItem value="NA">NA · No Answer</SelectItem>
                  <SelectItem value="WN">WN · Wrong Number</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-slate-400">Caller ID (E.164)</Label>
              <Input
                value={form.caller_id || ""}
                onChange={(e) => update({ caller_id: e.target.value })}
                placeholder="+974..."
                className="h-8 text-xs mt-1 font-mono"
                data-testid="dialer-caller-id"
              />
            </div>
          </div>

          {/* Genesys integration — Phase 7 */}
          <div className="pt-3 border-t">
            <GenesysConfigSection />
          </div>

          {form.updated_at && (
            <div className="text-[10px] text-slate-400 pt-1 flex items-center gap-1.5">
              <Clock className="w-3 h-3" />
              Last saved {new Date(form.updated_at).toLocaleString()} by {form.updated_by || "system"}.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function NumField({ label, value, onChange, min, max, testid, hint }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-slate-400">{label}</Label>
      <Input
        type="number"
        min={min}
        max={max}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        className="h-8 text-xs mt-1 font-mono"
        data-testid={testid}
      />
      {hint && <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}


// =====================================================
// Genesys Cloud CX per-tenant integration config + live status.
// Loaded and saved via /api/genesys/config; status polled via /api/genesys/status.
// =====================================================
function GenesysConfigSection() {
  const [cfg, setCfg] = useState(null);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([
        api.get("/genesys/config"),
        api.get("/genesys/status"),
      ]);
      setCfg({
        region_host: c.data.region_host || "mec1.pure.cloud",
        client_id: c.data.client_id || "",
        client_secret: "",
        contact_list_id: c.data.contact_list_id || "",
        campaign_id: c.data.campaign_id || "",
        queue_id: c.data.queue_id || "",
        inbound_webhook_secret: "",
        client_secret_set: !!c.data.client_secret_set,
        webhook_secret_set: !!c.data.webhook_secret_set,
      });
      setStatus(s.data);
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Genesys config");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = (patch) => { setCfg((f) => ({ ...f, ...patch })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        region_host: cfg.region_host,
        client_id: cfg.client_id,
        client_secret: cfg.client_secret || "",
        contact_list_id: cfg.contact_list_id || null,
        campaign_id: cfg.campaign_id || null,
        queue_id: cfg.queue_id || null,
      };
      if (cfg.inbound_webhook_secret) {
        payload.inbound_webhook_secret = cfg.inbound_webhook_secret;
      }
      await api.put("/genesys/config", payload);
      toast.success("Genesys config saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    try {
      const { data } = await api.get("/genesys/status");
      setStatus(data);
      if (data?.configured) toast.success("Genesys token exchange OK");
      else toast.warning(data?.reason || "Not configured");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status check failed");
    }
  };

  if (!cfg) return <div className="text-slate-400 text-xs">Loading Genesys config…</div>;

  const configured = !!status?.configured;

  return (
    <div className="space-y-3" data-testid="genesys-config">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Info className={`w-4 h-4 ${configured ? "text-emerald-600" : "text-slate-400"}`} />
          <div>
            <div className="text-sm font-semibold text-slate-800">Genesys Cloud CX</div>
            <div className={`text-[10px] font-medium ${configured ? "text-emerald-700" : "text-slate-500"}`}>
              {configured
                ? `Live · ${status?.region_host}`
                : (status?.reason || "Not configured")}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={testConnection} className="h-7 text-xs" data-testid="genesys-test-btn">
            Test connection
          </Button>
          <Button
            size="sm"
            onClick={save}
            disabled={!dirty || saving}
            className="h-7 bg-indigo-600 hover:bg-indigo-700 text-xs"
            data-testid="genesys-save-btn"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-400">Region host</Label>
          <Select value={cfg.region_host} onValueChange={(v) => update({ region_host: v })}>
            <SelectTrigger className="h-8 text-xs mt-1" data-testid="genesys-region">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mec1.pure.cloud">Middle East · mec1.pure.cloud</SelectItem>
              <SelectItem value="mypurecloud.com">US East · mypurecloud.com</SelectItem>
              <SelectItem value="mypurecloud.ie">EU West · mypurecloud.ie</SelectItem>
              <SelectItem value="mypurecloud.de">EU Central · mypurecloud.de</SelectItem>
              <SelectItem value="apne2.pure.cloud">Asia · apne2.pure.cloud</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-400">OAuth Client ID</Label>
          <Input
            value={cfg.client_id}
            onChange={(e) => update({ client_id: e.target.value })}
            className="h-8 text-xs mt-1 font-mono"
            data-testid="genesys-client-id"
          />
        </div>
        <div className="col-span-2">
          <Label className="text-[10px] uppercase tracking-wider text-slate-400">
            OAuth Client Secret {cfg.client_secret_set && <span className="ml-1 text-emerald-600 normal-case">· stored</span>}
          </Label>
          <Input
            type="password"
            value={cfg.client_secret}
            onChange={(e) => update({ client_secret: e.target.value })}
            placeholder={cfg.client_secret_set ? "•••••••••••••• (leave blank to keep)" : "Paste new secret"}
            className="h-8 text-xs mt-1 font-mono"
            data-testid="genesys-client-secret"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-400">Contact List ID</Label>
          <Input
            value={cfg.contact_list_id}
            onChange={(e) => update({ contact_list_id: e.target.value })}
            className="h-8 text-xs mt-1 font-mono"
            data-testid="genesys-contact-list"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-400">Campaign ID</Label>
          <Input
            value={cfg.campaign_id}
            onChange={(e) => update({ campaign_id: e.target.value })}
            className="h-8 text-xs mt-1 font-mono"
            data-testid="genesys-campaign-id"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-400">Callback Queue ID</Label>
          <Input
            value={cfg.queue_id}
            onChange={(e) => update({ queue_id: e.target.value })}
            className="h-8 text-xs mt-1 font-mono"
            data-testid="genesys-queue-id"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-slate-400">
            Webhook Secret {cfg.webhook_secret_set && <span className="ml-1 text-emerald-600 normal-case">· stored</span>}
          </Label>
          <Input
            type="password"
            value={cfg.inbound_webhook_secret}
            onChange={(e) => update({ inbound_webhook_secret: e.target.value })}
            placeholder={cfg.webhook_secret_set ? "•••••• (leave blank to keep)" : "For signature check"}
            className="h-8 text-xs mt-1 font-mono"
            data-testid="genesys-webhook-secret"
          />
        </div>
      </div>

      <div className="text-[10px] text-slate-500 leading-snug bg-slate-50 border border-slate-200 rounded p-2">
        <strong>Webhook URL:</strong> <span className="font-mono">POST /api/webhooks/genesys/&lt;tenant-slug&gt;</span><br />
        Configure this in Genesys → Admin → Webhook for Events with the tenant secret above appended as <span className="font-mono">?secret=</span> or sent as <span className="font-mono">X-Webhook-Secret</span>.
      </div>
    </div>
  );
}
